import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { NotificationService } from '../notification/notification.service';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import { ProductionQueue, ProductionService } from '../stokker/api';
import { ComponentKind, componentKindOf } from '../../../../backend/src/components/component-kind';

// ---------------------------------------------------------------------------
// A components/stock végpont sora (GetComponentStockQuantityWithBOM eljárás)
// ---------------------------------------------------------------------------
interface StockApiRow {
  id: number;
  name: string;
  sku: string;
  game: string | null;
  game_id: number | null;
  for_sale: 0 | 1;
  has_bom: 0 | 1;
  is_package_unit: 0 | 1;
  product_group: string | null;
  material_id: number | null;
  weight: string | number | null;
  printing_time: number | null;
  image_file: string | null;
  is_custom?: 0 | 1;
  /** a tárolt típus ('printed' | 'bought' | 'assembled'); régi soron még üres */
  kind?: ComponentKind | null;
  limit: number;
  /** elérhető készlet (a foglalások már levonva) */
  stock: number;
  /** aktív rendelések által foglalt mennyiség */
  reserved: number;
  /** a komponenshez rendelt anyag ára (Ft/kg) — az önköltséghez */
  material_price?: number | null;
}

type CompTypeKey = ComponentKind;
type StockLevel = 'ok' | 'warn' | 'crit';

interface CompRowVM {
  raw: StockApiRow;
  id: number;
  name: string;
  sku: string;
  imageFile: string | null;
  typeKey: CompTypeKey;
  typeLabel: string;
  /** egyedi (rendelésre gyártott) komponens — lila badge, nincs minimum */
  custom: boolean;
  isAssembled: boolean;
  weightLabel: string;
  timeLabel: string;
  costLabel: string;
  /** true, ha az anyagnak nincs ára, és a becsült alapárral számoltunk */
  costEstimated: boolean;
  /** fizikai készlet = elérhető + foglalt (a régi lista is így mutatta) */
  stock: number;
  minStock: number;
  level: StockLevel;
  stateLabel: string;
  minLabel: string;
  hasReserved: boolean;
  reservedLabel: string;
  reservedDanger: boolean;
  /** csak nyers komponensre / csomagolási egységre lehet közvetlenül készletet könyvelni */
  canManageStock: boolean;
  busy?: boolean;
}

interface BomChildVM {
  name: string;
  sku: string;
  qty: number;
  imageFile: string | null;
  stock: number;
  stockLabel: string;
  ok: boolean;
  enoughLabel: string;
  missing: boolean;
}

interface GameChipVM {
  label: string;
  dot: string | null;
}

/** A components/archived végpont sora. */
interface ArchivedApiRow {
  id: number;
  name: string;
  sku: string;
  image_file: string | null;
  kind?: ComponentKind | null;
  has_bom?: 0 | 1;
  printing_time?: number | null;
  material_id?: number | null;
  archived_at?: string | Date | null;
}

interface ArchivedRowVM {
  id: number;
  name: string;
  sku: string;
  imageFile: string | null;
  typeLabel: string;
  archivedLabel: string;
}

const COMP_TYPE_LABELS: { [key in ComponentKind]: string } = {
  printed: 'Nyomtatott',
  bought: 'Vásárolt',
  assembled: 'Összeépített',
};

// Önköltség (23-logic.js compCost): a komponenshez rendelt ANYAG ára (Ft/kg,
// az Anyagok képernyőről) × súly + a gépidő perc-díja (Beállítások →
// printPerMin) × nyomtatási idő. Ha valamelyik hiányzik, ezekkel számolunk.
const DEFAULT_MATERIAL_PRICE_FT_PER_KG = 3200;
const DEFAULT_MACHINE_COST_FT_PER_MIN = 1.5;

const KNOWN_DOT_COLORS: { [name: string]: string } = {
  'BGFox': '#9A6E9E',
  'Jump and Hide': '#5E7FA8',
};
const DOT_PALETTE = ['#9A6E9E', '#5E7FA8', '#8FBF6E', '#C98B4E', '#7A5E9E', '#4C7A2E', '#B4838D', '#5E9EA0'];

@Component({
  selector: 'app-components',
  templateUrl: './components.component.html',
  styleUrl: './components.component.scss'
})
export class ComponentsComponent implements OnInit {

  fetching = false;
  loadFailed = false;
  allComponents: StockApiRow[] = [];

  // Szűrők (a 23-logic.js componentsVals state-je)
  search = '';
  selectedGame = 'Mind';
  selectedCat = 'Mind';
  selectedType: 'Mind' | 'Nyomtatott' | 'Vásárolt' | 'Összeépített' = 'Mind';
  lowOnly = false;

  readonly typeOptions = [
    { value: 'Mind', label: 'Minden típus' },
    { value: 'Nyomtatott', label: 'Nyomtatott' },
    { value: 'Vásárolt', label: 'Vásárolt' },
    { value: 'Összeépített', label: 'Összeépített' },
  ];

  gameChips: GameChipVM[] = [{ label: 'Mind', dot: null }];
  catOptions: { value: string; label: string }[] = [];

  // Lapozás (kliens oldali, a teljes listán)
  pageSize = 20;
  pageIndex = 0;
  readonly pageSizeOptions: number[] = [20, 50, 100];

  filteredRows: CompRowVM[] = [];
  pagedRows: CompRowVM[] = [];

  // Nyomtatási várólista sáv (production/queue) — hiba/404 esetén rejtve
  queue: ProductionQueue | null = null;
  queueSummary = '';

  // Összeépített sorok lenyitása
  expandedIds = new Set<number>();
  bomChildren = new Map<number, BomChildVM[]>();
  bomLoading = new Set<number>();

  // Készlet módosítás modál
  stockModal: { row: CompRowVM; mode: 'add' | 'remove'; qty: string } | null = null;
  savingStock = false;

  // Archívum modál (a kivezetett komponensek)
  archiveOpen = false;
  archiveLoading = false;
  archivedRows: ArchivedRowVM[] = [];
  restoringId: number | null = null;

  /** Beállítások → Kalkuláció → printPerMin (Ft/perc gépidő). */
  machineCostPerMin = DEFAULT_MACHINE_COST_FT_PER_MIN;

  constructor(
    private http: HttpClient,
    private router: Router,
    private notificationService: NotificationService,
    private confirmDialog: ConfirmDialogService,
    private productionService: ProductionService,
  ) {}

  ngOnInit(): void {
    this.fetchCalculationSettings();
    this.fetchComponents();
    this.fetchQueue();
  }

  /** A gépidő perc-díja a Beállításokból (hiba esetén marad az alapérték). */
  private fetchCalculationSettings() {
    this.http.get<{ printPerMin?: number }>('settings/calculation').subscribe({
      next: (settings) => {
        const value = Number(settings?.printPerMin);
        if (Number.isFinite(value) && value >= 0) {
          this.machineCostPerMin = value;
          this.applyFilters(false);
        }
      },
      error: () => {
        // marad a DEFAULT_MACHINE_COST_FT_PER_MIN
      },
    });
  }

  // -------------------------------------------------------------------------
  // Adatlekérés
  // -------------------------------------------------------------------------

  fetchComponents() {
    this.fetching = true;
    // A stock végpont has_bom nélkül csak a nyers (has_bom=0) komponenseket
    // adja vissza, ezért az összeépítetteket külön kérjük le és fésüljük össze.
    forkJoin({
      raw: this.http.get<StockApiRow[]>('components/stock'),
      assembled: this.http.get<StockApiRow[]>('components/stock?has_bom=1'),
    }).subscribe({
      next: ({ raw, assembled }) => {
        this.allComponents = [...(raw || []), ...(assembled || [])];
        this.rebuildGameChips();
        this.loadFailed = false;
        this.fetching = false;
        this.applyFilters(false);
      },
      error: () => {
        this.allComponents = [];
        this.loadFailed = true;
        this.fetching = false;
        this.applyFilters(false);
        this.notificationService.show('Hiba a komponensek lekérdezése során!', 'danger');
      },
    });
  }

  fetchQueue() {
    this.productionService.getQueue().subscribe({
      next: (queue) => {
        if (queue && queue.tasks && queue.tasks.length > 0) {
          this.queue = queue;
          const hours = Math.round(queue.totalMinutes / 60 * 10) / 10;
          this.queueSummary = `${queue.tasks.length} komponens készletminimum alatt · ${queue.totalPieces} db nyomtatandó · ~${hours} óra · ${queue.totalGrams} g anyag`;
        } else {
          this.queue = null;
        }
      },
      error: () => {
        this.queue = null;
      },
    });
  }

  // -------------------------------------------------------------------------
  // Szűrés (1:1 a prototípus componentsVals logikájával)
  // -------------------------------------------------------------------------

  /**
   * Típus: a komponensen TÁROLT `kind` érték (a szerkesztőben beállított
   * típus). A migráció előtti sorokon a régi következtetés a tartalék —
   * ugyanaz a `componentKindOf`, amit a szerver is használ, hogy a lista, a
   * Gyártás és a Beszerzés ne mondhasson mást ugyanarról a tételről.
   */
  private typeKeyOf(c: StockApiRow): CompTypeKey {
    return componentKindOf(c as any);
  }

  private physicalStock(c: StockApiRow): number {
    return (c.stock || 0) + (c.reserved || 0);
  }

  private levelOf(stock: number, minStock: number): StockLevel {
    if (stock <= 0) return 'crit';
    if (stock <= minStock) return 'warn';
    return 'ok';
  }

  /** Az Anyagok képernyőn rögzített Ft/kg ár — a prod adatokban ma még mindenhol üres. */
  private materialPriceOf(c: StockApiRow): number | null {
    return c.material_price != null && Number.isFinite(Number(c.material_price))
      ? Number(c.material_price)
      : null;
  }

  /**
   * Igaz, ha az önköltség (vagy összeépítettnél valamelyik összetevőé) a
   * DEFAULT_MATERIAL_PRICE_FT_PER_KG alapárral készült, mert az anyaghoz nincs ár
   * rögzítve. Ezt a listában jeleznünk kell, különben pontos árként olvasnák.
   */
  private costIsEstimated(c: StockApiRow): boolean {
    const type = this.typeKeyOf(c);
    if (type === 'printed') {
      return this.materialPriceOf(c) === null;
    }
    if (type === 'assembled') {
      const children = this.bomChildren.get(c.id);
      if (!children || !children.length) return false;
      return children.some((child) => {
        const src = this.allComponents.find(x => x.sku === child.sku);
        return !!src && this.costIsEstimated(src);
      });
    }
    return false;
  }

  private costOf(c: StockApiRow): number | null {
    const type = this.typeKeyOf(c);
    if (type === 'printed') {
      const weight = parseFloat(String(c.weight ?? 0)) || 0;
      const minutes = c.printing_time || 0;
      if (weight <= 0 && minutes <= 0) return null;
      const materialPrice = this.materialPriceOf(c) ?? DEFAULT_MATERIAL_PRICE_FT_PER_KG;
      return Math.round(weight * materialPrice / 1000 + minutes * this.machineCostPerMin);
    }
    if (type === 'assembled') {
      // Csak akkor tudjuk, ha az összetevők már be vannak töltve (lenyitáskor).
      const children = this.bomChildren.get(c.id);
      if (!children || !children.length) return null;
      let sum = 0;
      for (const child of children) {
        const src = this.allComponents.find(x => x.sku === child.sku);
        if (!src) return null;
        const childCost = this.costOf(src);
        if (childCost === null) return null;
        sum += childCost * child.qty;
      }
      return sum;
    }
    return null; // vásárolt: nincs beszerzési ár mező a valós adatmodellben
  }

  /**
   * A komponensek egy részének (a prod adatokban 72 db) nincs beállított minimuma
   * (`limit = 0`). Ilyenkor a design „min. {N} db · +{K} kell" sablonja értelmetlen
   * címkét adna („min. 0 db · +13 kell"): nincs mihez képest hiány. Ezért itt a
   * tényleges (negatív) készletet mondjuk ki, minimum nélkül.
   */
  private minLabelOf(stock: number, minStock: number): string {
    if (minStock > 0) {
      return stock < minStock
        ? `min. ${minStock} db · +${minStock - stock} kell`
        : `min. ${minStock} db`;
    }
    return stock < 0
      ? `nincs minimum · ${-stock} db hiány`
      : 'nincs megadott minimum';
  }

  private stateLabelOf(level: StockLevel, minStock: number): string {
    if (level === 'crit') {
      return 'Elfogyott';
    }
    if (minStock <= 0) {
      return 'Készleten (nincs megadott minimum)';
    }
    return level === 'ok'
      ? `Készleten (min. ${minStock})`
      : `Minimum szint (min. ${minStock})`;
  }

  private buildRow(c: StockApiRow): CompRowVM {
    const typeKey = this.typeKeyOf(c);
    const stock = this.physicalStock(c);
    const minStock = c.limit || 0;
    const level = this.levelOf(stock, minStock);
    const cost = this.costOf(c);
    const loadedChildren = this.bomChildren.get(c.id);

    let weightLabel = '–';
    if (typeKey === 'printed' && c.weight != null) {
      weightLabel = `${parseFloat(String(c.weight))} g`;
    }

    let timeLabel = '–';
    if (typeKey === 'printed') {
      timeLabel = (c.printing_time || 0) > 0 ? `${c.printing_time} perc` : '–';
    } else if (typeKey === 'assembled') {
      timeLabel = loadedChildren ? `${loadedChildren.length} részből` : 'Összetevők';
    }

    const reserved = c.reserved || 0;

    return {
      raw: c,
      id: c.id,
      name: c.name,
      sku: c.sku,
      imageFile: c.image_file,
      typeKey,
      typeLabel: COMP_TYPE_LABELS[typeKey],
      custom: c.is_custom === 1,
      isAssembled: typeKey === 'assembled',
      weightLabel,
      timeLabel,
      costLabel: cost !== null ? `${cost} Ft` : '–',
      costEstimated: cost !== null && this.costIsEstimated(c),
      stock,
      minStock,
      level,
      stateLabel: this.stateLabelOf(level, minStock),
      // Egyedi (rendelésre gyártott) komponensnél nincs minimum-elvárás.
      minLabel: c.is_custom === 1 ? '' : this.minLabelOf(stock, minStock),
      hasReserved: reserved > 0,
      reservedLabel: `${reserved} db foglalt rendelésekhez`,
      reservedDanger: reserved > stock,
      canManageStock: c.is_package_unit === 1 || c.has_bom === 0,
    };
  }

  private rebuildGameChips() {
    const names = [...new Set(this.allComponents.map(c => c.game).filter((g): g is string => !!g))]
      .sort((a, b) => a.localeCompare(b, 'hu'));
    this.gameChips = [{ label: 'Mind', dot: null }, ...names.map((name, i) => ({
      label: name,
      dot: KNOWN_DOT_COLORS[name] || DOT_PALETTE[i % DOT_PALETTE.length],
    }))];
    if (this.selectedGame !== 'Mind' && !names.includes(this.selectedGame)) {
      this.selectedGame = 'Mind';
      this.selectedCat = 'Mind';
    }
  }

  private rebuildCatOptions() {
    if (this.selectedGame === 'Mind') {
      this.catOptions = [];
      return;
    }
    const cats = [...new Set(this.allComponents
      .filter(c => c.game === this.selectedGame && c.product_group)
      .map(c => c.product_group as string))]
      .sort((a, b) => a.localeCompare(b, 'hu'));
    this.catOptions = cats.length
      ? [{ value: 'Mind', label: 'Minden kategória' }, ...cats.map(c => ({ value: c, label: c }))]
      : [];
  }

  applyFilters(resetPage: boolean = true) {
    this.rebuildCatOptions();
    const q = this.search.trim().toLowerCase();

    const typeMatch = (typeKey: CompTypeKey) => this.selectedType === 'Mind'
      || (this.selectedType === 'Nyomtatott' && typeKey === 'printed')
      || (this.selectedType === 'Vásárolt' && typeKey === 'bought')
      || (this.selectedType === 'Összeépített' && typeKey === 'assembled');

    this.filteredRows = this.allComponents
      .filter(c => typeMatch(this.typeKeyOf(c))
        && (this.selectedGame === 'Mind' || c.game === this.selectedGame)
        && (this.selectedCat === 'Mind' || this.selectedGame === 'Mind' || c.product_group === this.selectedCat)
        && (!this.lowOnly || this.physicalStock(c) < (c.limit || 0))
        && (!q || c.name.toLowerCase().includes(q) || c.sku.toLowerCase().includes(q)))
      .map(c => this.buildRow(c));

    if (resetPage) {
      this.pageIndex = 0;
    }
    this.updatePage();
  }

  private updatePage() {
    const maxPage = Math.max(Math.ceil(this.filteredRows.length / this.pageSize) - 1, 0);
    if (this.pageIndex > maxPage) {
      this.pageIndex = maxPage;
    }
    const start = this.pageIndex * this.pageSize;
    this.pagedRows = this.filteredRows.slice(start, start + this.pageSize);
  }

  get footerLabel(): string {
    const base = `${this.filteredRows.length} komponens · az önköltség a kiválasztott anyag árából és a nyomtatási időből számolódik`;
    // Ha egyetlen látható sor önköltsége sem valós anyagárból jön, ne állítsuk azt,
    // hogy az „anyag árából” számolt — mondjuk meg, hogy becsült alapárral dolgozunk.
    const estimated = this.filteredRows.filter(r => r.costEstimated).length;
    if (!estimated) {
      return base;
    }
    return `${base} · ${estimated} komponensnél az anyaghoz nincs ár rögzítve, ezért ${DEFAULT_MATERIAL_PRICE_FT_PER_KG} Ft/kg becsült alapárral számolunk (✳)`;
  }

  get totalPages(): number {
    return Math.max(Math.ceil(this.filteredRows.length / this.pageSize), 1);
  }

  get pageRangeLabel(): string {
    if (!this.filteredRows.length) return '0 / 0';
    const start = this.pageIndex * this.pageSize + 1;
    const end = Math.min((this.pageIndex + 1) * this.pageSize, this.filteredRows.length);
    return `${start}–${end} / ${this.filteredRows.length}`;
  }

  // Szűrő események ----------------------------------------------------------

  onSearchChange(value: string) {
    this.search = value;
    this.applyFilters();
  }

  pickGame(name: string) {
    this.selectedGame = name;
    this.selectedCat = 'Mind';
    this.applyFilters();
  }

  onCatChange(value: string) {
    this.selectedCat = value;
    this.applyFilters();
  }

  onTypeChange(value: string) {
    this.selectedType = value as any;
    this.applyFilters();
  }

  toggleLowOnly() {
    this.lowOnly = !this.lowOnly;
    this.applyFilters();
  }

  prevPage() {
    if (this.pageIndex > 0) {
      this.pageIndex--;
      this.updatePage();
    }
  }

  nextPage() {
    if (this.pageIndex < this.totalPages - 1) {
      this.pageIndex++;
      this.updatePage();
    }
  }

  onPageSizeChange(value: string) {
    this.pageSize = parseInt(value, 10) || 20;
    this.pageIndex = 0;
    this.updatePage();
  }

  // -------------------------------------------------------------------------
  // Összeépített sor lenyitása (összetevők készlettel)
  // -------------------------------------------------------------------------

  toggleOpen(row: CompRowVM) {
    if (!row.isAssembled) {
      return;
    }
    if (this.expandedIds.has(row.id)) {
      this.expandedIds.delete(row.id);
      return;
    }
    this.expandedIds.add(row.id);
    if (!this.bomChildren.has(row.id) && !this.bomLoading.has(row.id)) {
      this.bomLoading.add(row.id);
      this.http.get<any>(`components/bom/${row.id}`).subscribe({
        next: (bom) => {
          const children: BomChildVM[] = (bom?.children || []).map((child: any) => {
            const src = this.allComponents.find(x => x.id === child.cid);
            const stock = src ? this.physicalStock(src) : 0;
            const qty = child.quantity || 1;
            const per = qty > 0 ? Math.floor(stock / qty) : 0;
            return {
              name: src?.name || child.component?.name || `#${child.cid}`,
              sku: src?.sku || child.component?.sku || '',
              qty,
              imageFile: src?.image_file || child.component?.image_file || null,
              stock,
              stockLabel: `${stock} db készleten`,
              ok: stock >= qty,
              enoughLabel: per > 0 ? `${per} egységhez elég` : 'ebből hiányzik',
              missing: per <= 0,
            };
          });
          this.bomLoading.delete(row.id);
          this.bomChildren.set(row.id, children);
          // frissítjük a "N részből" címkét és az összegzett önköltséget
          this.applyFilters(false);
        },
        error: () => {
          this.bomLoading.delete(row.id);
          this.expandedIds.delete(row.id);
          this.notificationService.show('Hiba történt az összetevők lekérése során!', 'danger');
        },
      });
    }
  }

  isOpen(row: CompRowVM): boolean {
    return this.expandedIds.has(row.id);
  }

  childrenOf(row: CompRowVM): BomChildVM[] {
    return this.bomChildren.get(row.id) || [];
  }

  // -------------------------------------------------------------------------
  // Készlet módosítás (Módosít gomb → modál → POST transactions)
  // -------------------------------------------------------------------------

  openStockModal(row: CompRowVM) {
    // Csak nyers komponensnek vagy csomagolási egységnek van saját készlete — az
    // összeépítettek készlete az összetevőkből számolódik. A gomb ilyenkor tiltott,
    // ez csak a védőháló.
    if (!row.canManageStock) {
      this.notificationService.show('Csak nyers komponenshez vagy csomagolási egységhez lehet készletet felvinni!', 'warning');
      return;
    }
    this.stockModal = { row, mode: 'add', qty: '' };
  }

  setStockMode(mode: 'add' | 'remove') {
    if (this.stockModal) {
      this.stockModal.mode = mode;
    }
  }

  onStockQtyInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const sanitized = input.value.replace(/[^0-9]/g, '');
    if (input.value !== sanitized) {
      input.value = sanitized;
    }
    if (this.stockModal) {
      this.stockModal.qty = sanitized;
    }
  }

  get stockModalQtyNum(): number {
    return this.stockModal ? parseInt(this.stockModal.qty || '0', 10) : 0;
  }

  get stockModalResult(): number {
    if (!this.stockModal) {
      return 0;
    }
    const delta = this.stockModal.mode === 'add' ? this.stockModalQtyNum : -this.stockModalQtyNum;
    return this.stockModal.row.stock + delta;
  }

  get stockModalValid(): boolean {
    // Pozitív darabszám, és kivételnél a készlet nem mehet 0 alá.
    return this.stockModalQtyNum > 0 && this.stockModalResult >= 0;
  }

  get stockModalPreview(): string {
    if (!this.stockModal || this.stockModalQtyNum <= 0) {
      return 'Add meg a darabszámot.';
    }
    if (this.stockModalResult < 0) {
      return `Ennyit nem lehet kivenni — a jelenlegi készlet ${this.stockModal.row.stock} db.`;
    }
    return `Új készlet mentés után: ${this.stockModalResult} db`;
  }

  closeStockModal() {
    if (!this.savingStock) {
      this.stockModal = null;
    }
  }

  confirmStockModal() {
    if (!this.stockModal || !this.stockModalValid || this.savingStock) {
      return;
    }
    const quantity = this.stockModal.mode === 'add' ? this.stockModalQtyNum : -this.stockModalQtyNum;
    this.savingStock = true;
    this.http.post<any>('transactions', {
      component_id: this.stockModal.row.id,
      quantity,
      comment: '',
    }).subscribe({
      next: () => {
        this.savingStock = false;
        this.stockModal = null;
        this.notificationService.show('Készlet módosítva!', 'success');
        this.fetchComponents();
        this.fetchQueue();
      },
      error: (e) => {
        this.savingStock = false;
        this.notificationService.show(e?.error?.message || 'Készlet módosítás sikertelen!', 'danger');
      },
    });
  }

  // -------------------------------------------------------------------------
  // Sor műveletek
  // -------------------------------------------------------------------------

  openComponentEditor() {
    this.router.navigate(['/components', '0']);
  }

  openEdit(row: CompRowVM) {
    this.router.navigate(['/components', row.id]);
  }

  duplicateRow(row: CompRowVM) {
    if (row.busy) {
      return;
    }
    row.busy = true;
    this.http.get<any>(`components/one/${row.id}`).subscribe({
      next: (c) => {
        const payload = {
          id: null,
          name: `${c.name} (másolat)`,
          game: c.game,
          game_id: c.game_id,
          material: c.material,
          material_id: c.material_id,
          product_group: c.product_group,
          product_group_id: c.product_group_id,
          for_sale: c.for_sale,
          has_bom: c.has_bom,
          // A másolat típusa (nyomtatott/vásárolt/összeépített) is az eredetié
          kind: c.kind,
          is_package_unit: c.is_package_unit,
          weight: c.weight,
          printing_time: c.printing_time,
          image_file: c.image_file,
          limit: c.limit,
          bom: (c.bom || []).map((b: any) => ({
            id: null,
            component_id: null,
            component_child_id: b.component_child_id ?? b.component_child?.id,
            quantity: b.quantity,
          })),
        };
        const formData = new FormData();
        formData.append('component', JSON.stringify(payload));
        this.http.post<any>('components/save', formData).subscribe({
          next: () => {
            row.busy = false;
            this.notificationService.show('Komponens duplikálva!', 'success');
            this.fetchComponents();
          },
          error: () => {
            row.busy = false;
            this.notificationService.show('Duplikálás sikertelen!', 'danger');
          },
        });
      },
      error: () => {
        row.busy = false;
        this.notificationService.show('Hiba a komponens lekérdezése során!', 'danger');
      },
    });
  }

  removeRow(row: CompRowVM) {
    this.confirmDialog.open({
      title: 'Komponens törlése',
      message: `A(z) „${row.name}” komponens kikerül a listából.`,
      // A megőrzendő előzményű tétel nem törölhető nyom nélkül — ilyenkor a
      // szerver archivál. Ezt előre megmondjuk, hogy ne érje meglepetés.
      details: 'Ha már tartozik hozzá készletmozgás vagy rendelés, a komponens nem törlődik, hanem archívumba kerül — az előzmény megmarad, és bármikor visszaállítható.',
      confirmLabel: 'Törlés',
      cancelLabel: 'Mégse',
      tone: 'danger',
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.http.delete<{ archived?: boolean; message?: string }>(`components/${row.id}`).subscribe({
        next: (res) => {
          this.notificationService.show(
            res?.message || (res?.archived ? 'A komponens archiválva.' : 'Törlés sikeres!'),
            res?.archived ? 'warning' : 'success',
          );
          this.fetchComponents();
          this.fetchQueue();
          if (this.archiveOpen) {
            this.fetchArchived();
          }
        },
        error: (e) => {
          this.notificationService.show(`Törlés sikertelen! ${e?.error?.message || ''}`.trim(), 'danger');
        },
      });
    });
  }

  // -------------------------------------------------------------------------
  // Archívum (kivezetett komponensek)
  // -------------------------------------------------------------------------

  openArchive() {
    this.archiveOpen = true;
    this.fetchArchived();
  }

  closeArchive() {
    this.archiveOpen = false;
  }

  fetchArchived() {
    this.archiveLoading = true;
    this.http.get<ArchivedApiRow[]>('components/archived').subscribe({
      next: (rows) => {
        this.archivedRows = (rows || []).map((row) => ({
          id: row.id,
          name: row.name,
          sku: row.sku,
          imageFile: row.image_file || null,
          typeLabel: COMP_TYPE_LABELS[componentKindOf(row as any)],
          archivedLabel: this.formatArchivedAt(row.archived_at),
        }));
        this.archiveLoading = false;
      },
      error: () => {
        this.archivedRows = [];
        this.archiveLoading = false;
        this.notificationService.show('Hiba az archivált komponensek lekérdezése során!', 'danger');
      },
    });
  }

  private formatArchivedAt(value: string | Date | null | undefined): string {
    if (!value) {
      return 'archiválva';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'archiválva';
    }
    return `archiválva: ${date.toLocaleDateString('hu-HU')}`;
  }

  restoreArchived(row: ArchivedRowVM) {
    if (this.restoringId) {
      return;
    }
    this.restoringId = row.id;
    this.http.patch<{ message?: string }>(`components/${row.id}/restore`, {}).subscribe({
      next: (res) => {
        this.restoringId = null;
        this.notificationService.show(res?.message || 'A komponens visszaállítva.', 'success');
        this.fetchArchived();
        this.fetchComponents();
        this.fetchQueue();
      },
      error: (e) => {
        this.restoringId = null;
        this.notificationService.show(e?.error?.message || 'Visszaállítás sikertelen!', 'danger');
      },
    });
  }

  trackRow(_index: number, row: CompRowVM) {
    return row.id;
  }
}
